import { useEffect } from "react";
import { alignMultisite4nkWebLineHostToPageLine } from "le-coffre-resources/dist/browserPublicHostPatterns.js";
import {
	rewriteBackApiHostWhenApiSubdomainMirrorsPage,
	rewriteLegacyLecoffreIoFrontUrlOnV2NotaryPage,
} from "@Front/Config/configRuntimeUrlHelpers";
import { FrontendVariables } from "@Front/Config/VariablesFront";
import { SessionRefreshService } from "@Front/Services/SessionRefreshService/SessionRefreshService";
import { isolateSessionContextForRoute } from "@Front/Hooks/helpers/bootstrapSessionContextIsolation";
import { useSessionContextRouteIsolation } from "@Front/Hooks/useSessionContextRouteIsolation";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { hydrateNotaryUserStoreFromCookiesIfPresent } from "@Front/Utils/notarySessionHydrationHelpers";
import { resolveRouteContextFamily } from "@Front/Utils/routeContextRegistry";

function applyBrowserRewritesToPublicEnv(instance: FrontendVariables): void {
	const browserHost = typeof window !== "undefined" ? window.location.host : "";
	if (browserHost) {
		instance.BACK_API_HOST = rewriteBackApiHostWhenApiSubdomainMirrorsPage(browserHost, instance.BACK_API_HOST);
	}
	instance.FRONT_APP_HOST = rewriteLegacyLecoffreIoFrontUrlOnV2NotaryPage(instance.FRONT_APP_HOST);
	if (browserHost) {
		instance.FRONT_APP_HOST = alignMultisite4nkWebLineHostToPageLine(browserHost, instance.FRONT_APP_HOST);
	}
}

function applyPublicEnvToFrontendVariables(instance: FrontendVariables): void {
	instance.BACK_API_PROTOCOL = process.env["NEXT_PUBLIC_BACK_API_PROTOCOL"] ?? "";
	instance.BACK_API_HOST = process.env["NEXT_PUBLIC_BACK_API_HOST"] ?? "";
	instance.BACK_API_ROOT_URL = process.env["NEXT_PUBLIC_BACK_API_ROOT_URL"] ?? "";
	instance.BACK_API_VERSION = process.env["NEXT_PUBLIC_BACK_API_VERSION"] ?? "";
	instance.FRONT_APP_HOST = process.env["NEXT_PUBLIC_FRONT_APP_HOST"] ?? "";
	applyBrowserRewritesToPublicEnv(instance);
	instance.IDNOT_BASE_URL = process.env["NEXT_PUBLIC_IDNOT_BASE_URL"] ?? "";
	instance.IDNOT_AUTHORIZE_ENDPOINT = process.env["NEXT_PUBLIC_IDNOT_AUTHORIZE_ENDPOINT"] ?? "";
	instance.IDNOT_CLIENT_ID = process.env["NEXT_PUBLIC_IDNOT_CLIENT_ID"] ?? "";
}

/**
 * Initializes FrontendVariables from NEXT_PUBLIC_* and starts session refresh.
 * Public site theme CSS is applied by `PublicSiteThemeEffect` under `PublicSiteConfigProvider`.
 */
export function useAppFrontendBootstrap(): void {
	useSessionContextRouteIsolation();

	useEffect(() => {
		let isDisposed = false;
		const instance = FrontendVariables.getInstance();
		applyPublicEnvToFrontendVariables(instance);

		void (async (): Promise<void> => {
			try {
				const pathname = typeof window !== "undefined" ? window.location.pathname : "";
				await isolateSessionContextForRoute(pathname, "bootstrap");
				if (resolveRouteContextFamily(pathname) === "notary") {
					await hydrateNotaryUserStoreFromCookiesIfPresent();
				}
			} catch (error) {
				LoggerService.error("❌ [SessionContextBootstrap] Failed to isolate mixed session context", { error });
			}
			if (!isDisposed) {
				SessionRefreshService.getInstance().start();
			}
		})();

		return (): void => {
			isDisposed = true;
			SessionRefreshService.getInstance().stop();
		};
	}, []);
}
